import React, { useContext, useEffect, useState, ReactNode } from 'react';
import { AuthState } from '@vis-auth/vis-auth';
import { useAuthService } from './AuthServiceContext';

const AuthStateContext = React.createContext<AuthState | null>(null);

export const useAuthState = (): AuthState => {
  const authState = useContext(AuthStateContext);
  if (!authState) {
    throw new Error('useAuthState must be used within <Security> or <AuthStateProvider>');
  }
  return authState;
};

export function AuthStateConsumer({ children }: { children: (authState: AuthState) => React.ReactNode }) {
  return (
    <AuthStateContext.Consumer>
      {(authState): ReactNode => {
        if (!authState) {
          throw new Error('AuthStateConsumer must be used within <Security> or <AuthServiceProvider>');
        }
        return children(authState);
      }}
    </AuthStateContext.Consumer>
  );
}

export function AuthStateProvider({ children }: { children?: React.ReactNode }) {
  const auth = useAuthService();
  const [authState, setAuthState] = useState(auth.state);
  useEffect(() => {
    return auth.subscribe(setAuthState);
  }, [auth, setAuthState]);
  return <AuthStateContext.Provider value={authState}>{children}</AuthStateContext.Provider>;
}
